import { useEffect, useMemo } from "react";
import { useAuth } from "react-oidc-context";

import KeycloakService from "#/data-layer/KeycloakService";
import * as DLD from "#/def/data-layer";
import env from "#/env";
import { useDataLayerRead } from "#/hooks/useDataLayer";
import { useTranslation } from "react-i18next";

/**
 * React hook for retrieving authentication-related information
 * about the currently logged-in user from Keycloak.
 *
 * The hook:
 * - Loads the user's configured credentials from the Keycloak Account API.
 * - Determines whether OTP/TOTP authentication is configured.
 * - Provides a helper for starting the OTP setup flow.
 *
 * @returns {{
 *   setupOTP: () => Promise<void>,
 *   isOtpConfigured: boolean | undefined
 * }} Authentication information and actions.
 */
export default function useLoginInfo() {
  const auth = useAuth();

  const { t } = useTranslation();
  const reader = useDataLayerRead({
    ident: DLD.KEYCLOAK,
    dataLayer: new KeycloakService(),
  });

  /**
   * Redirects the user to the Keycloak OTP configuration flow.
   *
   * @returns {Promise<void>}
   */
  const setupOTP = () => {
    return auth.signinRedirect({
      extraQueryParams: {
        kc_action: "CONFIGURE_TOTP",
        module: t(env.APP_MODULE),
      },
    });
  };

  useEffect(() => {
    if (auth?.user?.access_token) {
      reader.read(auth?.user?.access_token);
    }
  }, [auth?.user?.access_token]);

  const isOtpConfigured = useMemo(() => {
    if (!reader.item) {
      return undefined;
    }

    return reader.item.some(
      // we check for otp login type if user credential metadata is defined
      (c) => c.type === "otp" && c.userCredentialMetadatas?.length > 0
    );
  }, [reader.item]);

  return {
    setupOTP,
    isOtpConfigured,
  };
}
